import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Webcam from "react-webcam";
import {
  Camera,
  Upload,
  Loader2,
  RefreshCw,
  IdCard,
  CreditCard,
  User,
  X,
  CheckCircle,
  ArrowRight,
  AlertCircle,
  FileText,
} from "lucide-react";
import { userAPI } from "../../services/api";
import useLeadStage from "../../hooks/useLeadStage";
import { LEAD_STAGE, automationNodeLeadStageToRouteMap } from "../../Utils/constants";
import PageLoader from "../../components/Loader";
import Toast from "../../components/Toast";
import FlowFooter from "../../components/FlowFooter";

const curr_page_lead_stage = [LEAD_STAGE.VERIFY_AADHAAR_KYC];

const ACCEPT_PAN = "image/jpeg,image/jpg,image/png,image/webp,application/pdf";

function isCameraPermissionDenied(err) {
  if (err == null) return false;
  if (typeof err === "string") {
    return /not\s*allowed|permission\s*denied|denied|NotAllowedError/i.test(err);
  }
  const name = err.name || "";
  return name === "NotAllowedError" || name === "PermissionDeniedError";
}

/** Clears content below fixed site header (~100px in Common.css) */
const HEADER_OFFSET_CLASS = "pt-[104px] sm:pt-[108px]";

function dataUrlToFile(dataUrl, filename) {
  const parts = dataUrl.split(",");
  const mimeMatch = parts[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/jpeg";
  const bstr = atob(parts[1] || "");
  const u8 = new Uint8Array(bstr.length);
  for (let i = 0; i < bstr.length; i += 1) {
    u8[i] = bstr.charCodeAt(i);
  }
  return new File([u8], filename, { type: mime });
}

function PanSlot({ label, file, onFile, inputRef }) {
  const previewUrl = useMemo(() => {
    if (!file || !file.type.startsWith("image/")) return null;
    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const isPdf = file?.type === "application/pdf";

  return (
    <div className="flex flex-col min-h-0 flex-1 bg-slate-50/90 border border-slate-200 rounded-2xl p-3 sm:p-4">
      <div className="flex items-center gap-2 text-xs font-semibold text-[#1E3A8A] shrink-0 mb-3">
        <IdCard className="w-4 h-4 text-[#1E3A8A]/80 shrink-0" />
        {label}
      </div>
      <div className="flex-1 min-h-0 flex flex-col">
        {!file ? (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex-1 min-h-[140px] sm:min-h-[160px] flex flex-col items-center justify-center gap-2 py-4 border-2 border-dashed border-slate-200 rounded-xl bg-white hover:border-slate-300 hover:bg-slate-50/80 transition-colors"
          >
            <Upload className="w-8 h-8 text-slate-400" />
            <span className="text-sm font-medium text-slate-700">Add file</span>
            <span className="text-xs text-slate-500 text-center px-2">JPG, PNG, WebP or PDF</span>
          </button>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col gap-3">
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shrink-0 shadow-sm">
              {previewUrl ? (
                <div className="relative bg-slate-100">
                  <img
                    src={previewUrl}
                    alt={`${label} preview`}
                    className="w-full max-h-[200px] sm:max-h-[220px] object-contain mx-auto block"
                  />
                  <div className="absolute top-2 right-2 bg-white/95 rounded-full p-0.5 shadow border border-slate-200">
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 py-10 px-4 text-center">
                  <FileText className="w-10 h-10 text-slate-400" />
                  <p className="text-sm font-medium text-slate-700">PDF attached</p>
                  <p className="text-xs text-slate-500">Preview not available for PDF</p>
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 shrink-0">
              <div className="min-w-0 text-left">
                <p className="text-xs font-medium text-slate-900 truncate">{file.name}</p>
                <p className="text-[11px] text-slate-500">
                  {isPdf ? "PDF" : "Image"} · {(file.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="shrink-0 self-start sm:self-center px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-medium hover:bg-slate-50 hover:border-slate-300 hover:text-slate-900 transition-colors"
              >
                Change file
              </button>
            </div>
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_PAN}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) onFile(f);
        }}
      />
    </div>
  );
}

export default function KycDocuments() {
  const navigate = useNavigate();
  const { leadStage, isLoadingStage } = useLeadStage();
  const [panFront, setPanFront] = useState(null);
  const [panBack, setPanBack] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [cameraPermissionDenied, setCameraPermissionDenied] = useState(false);
  const [webcamInstanceKey, setWebcamInstanceKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "error" });
  const [showMandatoryModal, setShowMandatoryModal] = useState(false);
  const frontRef = useRef(null);
  const backRef = useRef(null);
  const webcamRef = useRef(null);

  const showToast = useCallback((message, type = "error") => {
    setToast({ show: true, message, type });
  }, []);
  const hideToast = useCallback(() => setToast({ show: false, message: "", type: "error" }), []);

  useEffect(() => {
    if (isLoadingStage) return;
    if (leadStage && !curr_page_lead_stage.includes(leadStage)) {
      const route = automationNodeLeadStageToRouteMap[leadStage];
      if (route) navigate(route);
    }
  }, [leadStage, isLoadingStage, navigate]);

  const openCamera = useCallback(() => {
    setCameraError("");
    setCameraPermissionDenied(false);
    setIsCameraReady(false);
    if (!navigator?.mediaDevices?.getUserMedia) {
      const message = "Camera is not supported on this device or browser.";
      setCameraError(message);
      showToast(message, "error");
      return;
    }
    setWebcamInstanceKey((k) => k + 1);
    setIsCameraOpen(true);
  }, [showToast]);

  const closeCamera = useCallback(() => {
    setIsCameraOpen(false);
    setIsCameraReady(false);
    setCameraError("");
    setCameraPermissionDenied(false);
  }, []);

  /** Remounts Webcam so react-webcam calls getUserMedia again (shows prompt when state is “prompt”). */
  const retryCameraAccess = useCallback(() => {
    setCameraError("");
    setCameraPermissionDenied(false);
    setIsCameraReady(false);
    setWebcamInstanceKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!isCameraOpen || !navigator.permissions?.query) return undefined;

    let cancelled = false;
    const permHolder = { status: null };

    const onPermissionChange = () => {
      const status = permHolder.status;
      if (cancelled || !status) return;
      if (status.state === "granted") {
        setCameraError("");
        setCameraPermissionDenied(false);
        setIsCameraReady(false);
        setWebcamInstanceKey((k) => k + 1);
      }
      if (status.state === "denied") {
        setCameraPermissionDenied(true);
        setCameraError(
          "Camera is blocked for this site. Allow it in your browser’s site settings (lock or tune icon next to the address bar), then tap “Request camera access again”."
        );
      }
    };

    (async () => {
      try {
        const status = await navigator.permissions.query({ name: "camera" });
        if (cancelled) return;
        permHolder.status = status;
        status.addEventListener("change", onPermissionChange);
        if (status.state === "denied") {
          setCameraPermissionDenied(true);
          setCameraError(
            "Camera is blocked for this site. Allow it in your browser’s site settings, then tap “Request camera access again”."
          );
        }
      } catch {
        /* Safari / Firefox may not support the “camera” permission name */
      }
    })();

    return () => {
      cancelled = true;
      if (permHolder.status) {
        permHolder.status.removeEventListener("change", onPermissionChange);
      }
    };
  }, [isCameraOpen]);

  const clearSelfie = useCallback(() => {
    setSelfieFile(null);
    setSelfiePreview(null);
    setCameraError("");
    setCameraPermissionDenied(false);
  }, []);

  const handleCaptureSelfie = useCallback(() => {
    if (!webcamRef.current || !isCameraReady) {
      showToast("Camera is not ready yet. Allow permission and try again.", "error");
      return;
    }
    const shot = webcamRef.current.getScreenshot();
    if (!shot) {
      showToast("Could not capture from camera. Try again.", "error");
      return;
    }
    setSelfiePreview(shot);
    const file = dataUrlToFile(shot, `selfie-${Date.now()}.jpg`);
    setSelfieFile(file);
    setCameraError("");
    setCameraPermissionDenied(false);
    closeCamera();
    showToast("Selfie captured successfully.", "success");
  }, [closeCamera, isCameraReady, showToast]);

  const retakeSelfie = useCallback(() => {
    clearSelfie();
    openCamera();
  }, [clearSelfie, openCamera]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    hideToast();
    if (!panFront || !panBack || !selfieFile) {
      setShowMandatoryModal(true);
      return;
    }
    setSubmitting(true);
    try {
      const res = await userAPI.uploadKYCDocuments({
        panFront,
        panBack,
        selfie: selfieFile,
      });
      if (res?.success) {
        navigate("/apply/references");
        return;
      }
      showToast(res?.message || "Upload could not be completed.");
    } catch (err) {
      showToast(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoadingStage || !leadStage) {
    return <PageLoader />;
  }

  if (!curr_page_lead_stage.includes(leadStage)) {
    return <PageLoader />;
  }

  const videoConstraints = {
    facingMode: "user",
    width: { ideal: 720 },
    height: { ideal: 960 },
  };

  return (
    <div
      className={`min-h-screen flex flex-col justify-between bg-gradient-to-b from-slate-50 to-white text-slate-800 relative ${HEADER_OFFSET_CLASS} pb-28 sm:pb-32`}
    >
      <div className="flex-1 w-full">
        <div className="relative z-10 flex flex-col min-h-[calc(100dvh-7rem)]">
          <div className="flex flex-1 justify-center px-3 sm:px-4 py-6 md:py-8">
            <div className="w-full max-w-4xl">
              <div className="text-center mb-6 md:mb-8">
                <h1 className="text-xl md:text-2xl font-bold text-[#1E3A8A] mb-2">
                  KYC documents
                </h1>
                <p className="text-sm md:text-base text-slate-600 max-w-lg mx-auto leading-relaxed">
                  Upload clear photos of both sides of your PAN, then take a selfie for verification. Your data is
                  encrypted and used only for KYC.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-8">
                {/* PAN — same card shell as reference “PAN card” block; contains front + back */}
                <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-5 md:p-7 flex flex-col">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-xl bg-[#1E3A8A] flex items-center justify-center shrink-0">
                      <CreditCard className="h-5 w-5 text-white" />
                    </div>
                    <div className="text-left">
                      <h2 className="font-semibold text-slate-900 text-base">PAN card</h2>
                      <p className="text-xs text-slate-500">Front and back — image or PDF</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1">
                    <PanSlot label="Front" file={panFront} onFile={setPanFront} inputRef={frontRef} />
                    <PanSlot label="Back" file={panBack} onFile={setPanBack} inputRef={backRef} />
                  </div>

                  {panFront && panBack && (
                    <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200/80 rounded-xl px-3 py-2 mt-4 text-center font-medium">
                      <span className="inline-flex items-center gap-1 justify-center">
                        <CheckCircle className="w-3.5 h-3.5 shrink-0" />
                        Both PAN sides added
                      </span>
                    </p>
                  )}
                </div>

                {/* Selfie — mirror reference right column */}
                <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-5 md:p-7 flex flex-col">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-xl bg-[#FF6B35] flex items-center justify-center shrink-0">
                      <User className="h-5 w-5 text-white" />
                    </div>
                    <div className="text-left">
                      <h2 className="font-semibold text-slate-900 text-base">Selfie</h2>
                      <p className="text-xs text-slate-500">Face the camera, good lighting</p>
                    </div>
                  </div>

                  {selfieFile && selfiePreview ? (
                    <div className="space-y-3 flex-1 flex flex-col">
                      <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-4 text-center">
                        <p className="text-xs font-medium text-slate-700 mb-2">Selfie preview</p>
                        <div className="relative inline-block mx-auto max-w-[220px] w-full aspect-[3/4] rounded-xl overflow-hidden border border-slate-200 bg-white shadow-sm">
                          <img src={selfiePreview} alt="Your selfie" className="w-full h-full object-cover" />
                          <div className="absolute top-2 right-2 bg-white/95 rounded-full p-0.5 shadow border border-slate-200">
                            <CheckCircle className="h-4 w-4 text-emerald-600" />
                          </div>
                        </div>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-white p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="min-w-0 text-left">
                          <p className="text-sm font-medium text-slate-900 truncate">{selfieFile.name}</p>
                          <p className="text-xs text-slate-500">
                            {(selfieFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={retakeSelfie}
                            disabled={submitting}
                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-medium hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 inline-flex items-center gap-1.5"
                          >
                            <RefreshCw className="h-3.5 w-3.5" />
                            Retake
                          </button>
                          <button
                            type="button"
                            onClick={clearSelfie}
                            disabled={submitting}
                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-medium hover:bg-red-50 hover:border-red-200 hover:text-red-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                          >
                            <X className="h-3.5 w-3.5" />
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={openCamera}
                      disabled={submitting}
                      className="flex-1 min-h-[180px] border-2 border-dashed border-slate-200 rounded-2xl flex flex-col items-center justify-center gap-2 hover:border-slate-300 hover:bg-slate-50/80 transition-colors disabled:opacity-50"
                    >
                      <Camera className="h-10 w-10 text-slate-400" />
                      <span className="font-medium text-slate-700">Take selfie</span>
                      <span className="text-xs text-slate-500">Opens your camera</span>
                    </button>
                  )}

                  {cameraError && !isCameraOpen && (
                    <p className="text-red-600 text-xs mt-2 text-center">{cameraError}</p>
                  )}
                </div>
              </div>


              <div className="text-center">
                <form onSubmit={handleSubmit}>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] hover:from-[#f55d28] hover:to-[#ff7a33] text-white font-semibold py-3.5 px-10 rounded-2xl shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-base"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin shrink-0" />
                        Uploading…
                      </>
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="h-5 w-5 shrink-0" />
                      </>
                    )}
                  </button>
                </form>
              </div>

              <p className="text-center text-xs text-slate-500 mt-6 max-w-md mx-auto leading-relaxed">
                Encrypted upload. We use these only for identity verification as per RBI guidelines.
              </p>
            </div>
          </div>
        </div>
      </div>

      {isCameraOpen && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[2000] p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden border border-slate-100">
            <div className="bg-[#1E3A8A] p-5 text-white">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-bold">Take your selfie</h3>
                  <p className="text-blue-100 text-sm mt-1">Look at the camera; ensure your face is well lit.</p>
                </div>
                <button
                  type="button"
                  onClick={closeCamera}
                  disabled={submitting}
                  className="p-2 rounded-full hover:bg-white/10 disabled:opacity-50 shrink-0"
                  aria-label="Close"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-5">
              {/* Portrait viewport: video is letterboxed by some drivers — center + min scale to cover */}
              <div className="relative mx-auto w-full max-w-[280px] rounded-2xl overflow-hidden bg-black aspect-[3/4] border border-slate-200 shadow-inner isolate">
                <Webcam
                  key={webcamInstanceKey}
                  ref={webcamRef}
                  audio={false}
                  mirrored
                  screenshotFormat="image/jpeg"
                  videoConstraints={videoConstraints}
                  onUserMedia={() => {
                    setIsCameraReady(true);
                    setCameraError("");
                    setCameraPermissionDenied(false);
                  }}
                  onUserMediaError={(err) => {
                    setIsCameraReady(false);
                    const denied = isCameraPermissionDenied(err);
                    setCameraPermissionDenied(denied);
                    const message = denied
                      ? "Camera access was denied. Allow the camera for this site in your browser settings, then tap “Request camera access again” below."
                      : typeof err === "string"
                        ? err
                        : err?.message || "Unable to access the camera. Check that no other app is using it and try again.";
                    setCameraError(message);
                    showToast(denied ? "Camera permission denied — you can enable it in site settings and try again." : message, "error");
                  }}
                  style={{
                    display: "block",
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    minWidth: "100%",
                    minHeight: "100%",
                    width: "auto",
                    height: "auto",
                    objectFit: "cover",
                    objectPosition: "center center",
                    transform: "translate(-50%, -50%)",
                  }}
                />
              </div>

              {(cameraError || cameraPermissionDenied) && (
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-left space-y-3">
                  <p className="text-xs text-amber-950 leading-snug">{cameraError}</p>
                  {cameraPermissionDenied && (
                    <ol className="text-[11px] text-amber-900/90 list-decimal pl-4 space-y-1.5 leading-relaxed">
                      <li>
                        <strong>Chrome / Edge:</strong> click the lock or tune icon in the address bar → Permissions → Camera → Allow, then return to this tab.
                      </li>
                      <li>
                        <strong>Safari (Mac):</strong> Safari → Settings for This Website → Camera → Allow.
                      </li>
                      <li>
                        <strong>iPhone / iPad:</strong> Settings → Safari → Camera, or use the “aA” menu → Website Settings → Camera → Allow.
                      </li>
                    </ol>
                  )}
                  <button
                    type="button"
                    onClick={retryCameraAccess}
                    disabled={submitting}
                    className="w-full py-2.5 rounded-xl border border-[#1E3A8A] bg-white text-[#1E3A8A] text-sm font-semibold hover:bg-slate-50 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4 shrink-0" />
                    Request camera access again
                  </button>
                </div>
              )}

              <div className="flex gap-3 mt-4">
                <button
                  type="button"
                  onClick={closeCamera}
                  disabled={submitting}
                  className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-800 font-medium hover:bg-slate-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCaptureSelfie}
                  disabled={submitting || !isCameraReady}
                  className="flex-1 py-3 rounded-xl bg-[#FF6B35] text-white font-medium hover:bg-[#f55d28] disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Camera className="h-5 w-5 shrink-0" />
                  Capture
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showMandatoryModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4"
          onClick={() => setShowMandatoryModal(false)}
          role="presentation"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 border border-slate-100"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="text-center">
              <div className="bg-amber-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="h-7 w-7 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Required</h3>
              <p className="text-slate-600 text-sm mb-6">
                Please add PAN front, PAN back, and a selfie to continue.
              </p>
              <button
                type="button"
                onClick={() => setShowMandatoryModal(false)}
                className="w-full bg-[#1E3A8A] text-white font-medium py-3 px-4 rounded-xl hover:bg-[#172554] transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {submitting && (
        <div className="fixed inset-0 bg-black/55 backdrop-blur-[2px] flex flex-col items-center justify-center z-[2100]">
          <Loader2 className="w-10 h-10 text-white animate-spin mb-2" />
          <p className="text-white text-xs font-medium px-4 text-center">Uploading…</p>
        </div>
      )}

      {toast.show && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      <FlowFooter />
    </div>
  );
}
